import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MessageCircle,
  Calendar,
  MapPin,
  Globe,
  Clock,
  Send,
  Rocket,
  Lightbulb,
  ArrowRight,
} from "lucide-react";
import FooterNew from "../components/FooterNew";
import { getBackendUrl } from "../utils/auth";

const contactCards = [
  { icon: Mail, title: "Email Us", desc: "For general inquiries and project discussions", info: "info@clientcodeai.in", sub: "Response within 24 hours" },
  { icon: Phone, title: "Call Us", desc: "Speak directly with our solutions team", info: "+91-9958821387", sub: "Mon – Sat: 10:00 AM – 7:00 PM" },
  { icon: MessageCircle, title: "Live Chat", desc: "Get instant answers to your questions", info: "Chat with us", sub: "Available during business hours" },
  { icon: Calendar, title: "Schedule a Call", desc: "Book a consultation with our AI experts", info: "Book Now", sub: "30-minute free consultation" },
];

const Contact = () => {
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const inputClassName = "w-full px-4 py-3 rounded-xl bg-surface/50 text-white placeholder-slate-500 border border-[#0076a3] focus:border-[#0076a3] focus:ring-1 focus:ring-[#0076a3] text-sm transition-colors";
  const selectClassName = "w-full px-4 py-3 rounded-xl bg-surface/50 text-slate-300 border border-[#0076a3] focus:border-[#0076a3] focus:ring-1 focus:ring-[#0076a3] text-sm transition-colors appearance-none cursor-pointer";
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", service: "", budget: "", message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setStatus({ type: "success", message: data.message || "Message sent successfully!" });
        setFormData({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
      } else {
        setStatus({ type: "error", message: data.message || "Failed to send message." });
      }
    } catch {
      setStatus({ type: "error", message: "Unable to connect to the server. Please try again later." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 sm:pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: "radial-gradient(circle, #7C4DFF, transparent)" }} />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ background: "radial-gradient(circle, #27B8FF, transparent)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.5rem] font-bold text-white mb-5 leading-tight">
              Get In <span className="gradient-text">Touch</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Ready to transform your business with AI? Let's start the conversation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
              whileHover={{ y: -4 }}
              className="rounded-2xl p-6 text-center group"
              style={{ background: "rgba(10, 16, 41, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
            >
              <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <card.icon className="w-6 h-6 text-ai-glow" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{card.title}</h3>
              <p className="text-sm text-slate-400 mb-3">{card.desc}</p>
              <p className="text-accent-light font-semibold text-sm mb-1">{card.info}</p>
              <p className="text-xs text-slate-500">{card.sub}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3 rounded-2xl p-8"
              style={{ background: "rgba(10, 16, 41, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
            >
              <h3 className="text-2xl font-bold text-white mb-2">Send Us a Message</h3>
              <p className="text-slate-400 text-sm mb-8">We'll get back to you within 24 hours.</p>

              <form className="space-y-5" onSubmit={handleSubmit}>
                {status.message && (
                  <div className={`px-4 py-3 rounded-xl text-sm font-medium ${status.type === "success" ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
                    {status.message}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Full Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required className={inputClassName} />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Email Address *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your email address" required className={inputClassName} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Phone Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" className={inputClassName} />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Company Name</label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} placeholder="Company name" className={inputClassName} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Service Interest *</label>
                    <select name="service" value={formData.service} onChange={handleChange} required className={selectClassName}>
                      <option value="">Select a service</option>
                      <option>Generative AI</option>
                      <option>Machine Learning</option>
                      <option>AI Consulting</option>
                      <option>Custom AI Solutions</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 font-medium mb-2">Project Budget</label>
                    <select name="budget" value={formData.budget} onChange={handleChange} className={selectClassName}>
                      <option value="">Select budget range</option>
                      <option>Below &#x20B9;1L</option>
                      <option>&#x20B9;1L &ndash; &#x20B9;5L</option>
                      <option>&#x20B9;5L &ndash; &#x20B9;10L</option>
                      <option>&#x20B9;10L+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 font-medium mb-2">Your Message *</label>
                  <textarea rows="4" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project, challenges, or questions..." required className={`${inputClassName} resize-none`} />
                </div>
                <button type="submit" disabled={submitting} className="neon-btn neon-btn-primary w-full justify-center">
                  <Send className="w-4 h-4" />
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 space-y-6"
            >
              <div className="rounded-2xl p-6" style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}>
                <h4 className="text-white font-semibold mb-4">Contact Information</h4>
                <div className="space-y-4">
                  {[
                    { icon: MapPin, label: "Registered Office", value: "A.D Nagar, Ballabgarh, Faridabad, Haryana 121004" },
                    { icon: MapPin, label: "Corporate Office", value: "First Floor 102, SRS Corporate Tower, NH-19, Sector 31, Faridabad 121003" },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <item.icon className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-slate-500 mb-1">{item.label}</div>
                        <div className="text-sm text-slate-300">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl p-6" style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}>
                <h4 className="text-white font-semibold mb-4">Business Hours</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-accent" /> Mon &ndash; Sat: 10:00 AM &ndash; 7:00 PM</div>
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-accent" /> Sunday: Closed</div>
                  <div className="flex items-center gap-2 text-ai-glow"><Clock className="w-4 h-4" /> Emergency Support: 24/7</div>
                </div>
              </div>

              <div className="rounded-2xl p-6" style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}>
                <h4 className="text-white font-semibold mb-4">Quick Contacts</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-400"><Mail className="w-4 h-4 text-accent" /> info@clientcodeai.in</div>
                  <div className="flex items-center gap-2 text-slate-400"><Mail className="w-4 h-4 text-accent" /> sales@clientcodeai.com</div>
                  <div className="flex items-center gap-2 text-slate-400"><Phone className="w-4 h-4 text-accent" /> +91-9958821387</div>
                  <div className="flex items-center gap-2 text-slate-400"><Globe className="w-4 h-4 text-accent" /> www.clientcodeai.com</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: "What is your typical response time?", a: "We respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly." },
              { q: "Do you offer free consultations?", a: "Yes, we offer a free 30-minute consultation to discuss your project requirements and explore potential AI solutions." },
              { q: "What industries do you serve?", a: "We serve diverse industries including finance, healthcare, retail, manufacturing, logistics, education, and more." },
              { q: "What is your typical project timeline?", a: "Project timelines vary based on complexity. Simple projects take 4\u20138 weeks, while comprehensive AI solutions may take 3\u20136 months." },
              { q: "Do you work with international clients?", a: "Yes, we work with clients globally and accommodate different time zones via video conference." },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="rounded-xl p-6"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <h4 className="text-white font-semibold mb-2">{faq.q}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Rocket, title: "Start a Project", desc: "Have a specific AI project in mind?", btn: "Get Quote", primary: true },
                { icon: Lightbulb, title: "Need Advice?", desc: "Not sure where to start with AI?", btn: "Book Consultation", primary: false },
                { icon: ArrowRight, title: "View Our Work", desc: "See our successful AI implementations", btn: "View Projects", primary: false, to: "/projects" },
              ].map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-6 text-center"
                  style={{
                    background: "linear-gradient(135deg, rgba(124, 77, 255, 0.1), rgba(39, 184, 255, 0.05))",
                    border: "1px solid rgba(124, 77, 255, 0.15)",
                  }}
                >
                  <card.icon className="w-8 h-8 text-ai-glow mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{card.title}</h3>
                  <p className="text-sm text-slate-400 mb-4">{card.desc}</p>
                  {card.to ? (
                    <Link to={card.to} className={`neon-btn ${card.primary ? "neon-btn-primary" : "neon-btn-outline"} text-sm justify-center w-full no-underline`}>
                      {card.btn}
                    </Link>
                  ) : (
                    <button className={`neon-btn ${card.primary ? "neon-btn-primary" : "neon-btn-outline"} text-sm justify-center w-full`}>
                      {card.btn}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <FooterNew />
    </main>
  );
};

export default Contact;
